import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, X, Upload, ShoppingBag, FileUp, Trash, Copy, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const BADGES = ["", "Nouveau", "Bestseller", "Promotion"];

const empty = {
  name: "", description: "", category: "Livres & E-books", price: "", old_price: "",
  badge: "", format: "", images: [], variants: [], digital_file: "",
  product_type: "digital", stock: "", status: "published", active: true, archived: false,
};

const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-brown-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="shop-admin-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold/20 sticky top-0 bg-white z-10">
          <h3 className="font-heading text-2xl font-medium text-brown-black">{title}</h3>
          <button onClick={onClose} className="p-1.5 text-brown-black/50 hover:text-brown-black"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const Lbl = ({ children }) => <label className="block text-xs uppercase tracking-[0.12em] text-brown-dark/60 font-semibold mb-1.5">{children}</label>;
const inputCls = "w-full border border-gold/30 rounded-lg px-3.5 py-2.5 focus:border-gold focus:outline-none transition-colors";

const ShopAdmin = () => {
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/shop/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setModal(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name, description: p.description || "", category: p.category || "Général",
      price: p.price, old_price: p.old_price || "", badge: p.badge || "", format: p.format || "",
      images: p.images || [], variants: (p.variants || []).map((v) => ({ ...v })),
      digital_file: p.digital_file || "", product_type: p.product_type || "digital",
      stock: p.stock ?? "", status: p.status || "published",
      active: p.active !== false, archived: Boolean(p.archived),
    });
    setEditId(p.id); setModal(true);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/shop/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, r.data.url] }));
      toast.success("Image ajoutée");
    } catch { toast.error("Échec du téléversement"); } finally { setBusy(false); }
  };

  const uploadDigital = async (file, cb) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/shop/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      cb(r.data.url, r.data.filename);
      toast.success("Fichier téléversé");
    } catch { toast.error("Échec du téléversement"); } finally { setBusy(false); }
  };

  const addVariant = () => setForm((f) => ({ ...f, variants: [...f.variants, { label: "", price: "", file: "" }] }));
  const updVariant = (i, key, val) => setForm((f) => ({ ...f, variants: f.variants.map((v, idx) => idx === i ? { ...v, [key]: val } : v) }));
  const rmVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name, description: form.description, category: form.category,
      price: parseFloat(form.price) || 0, old_price: parseFloat(form.old_price) || 0,
      badge: form.badge, format: form.format, images: form.images, digital_file: form.digital_file,
      product_type: form.product_type, stock: parseInt(form.stock, 10) || 0, status: form.status,
      active: form.active, archived: form.archived,
      variants: form.variants.filter((v) => v.label).map((v) => ({ label: v.label, price: parseFloat(v.price) || 0, file: v.file || "" })),
    };
    try {
      if (editId) await api.put(`/admin/shop/products/${editId}`, payload);
      else await api.post("/admin/shop/products", payload);
      toast.success(editId ? "Produit mis à jour" : "Produit ajouté");
      setModal(false); load();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement ce produit ?")) return;
    await api.delete(`/admin/shop/products/${id}`); toast.success("Produit supprimé"); load();
  };
  const duplicate = async (id) => {
    try {
      await api.post(`/admin/shop/products/${id}/duplicate`);
      toast.success("Produit dupliqué (en brouillon)"); load();
    } catch { toast.error("Erreur lors de la duplication"); }
  };

  const [fileBusy, setFileBusy] = useState(null);
  const quickUploadFile = async (p, file) => {
    if (!file) return;
    setFileBusy(p.id);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/admin/shop/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const payload = {
        name: p.name, description: p.description || "", category: p.category || "Général",
        price: p.price, old_price: p.old_price || 0, badge: p.badge || "", format: p.format || "",
        images: p.images || [], variants: p.variants || [], digital_file: up.data.url,
        external_link: p.external_link || "", product_type: p.product_type || "digital",
        stock: p.stock || 0, status: p.status || "published", active: p.active !== false, archived: !!p.archived,
      };
      await api.put(`/admin/shop/products/${p.id}`, payload);
      toast.success("Fichier associé au produit", { description: p.name }); load();
    } catch { toast.error("Échec du téléversement du fichier"); }
    finally { setFileBusy(null); }
  };
  const toggleArchive = async (p) => {
    await api.put(`/admin/shop/products/${p.id}`, { ...p, archived: !p.archived });
    toast.success(p.archived ? "Produit réactivé" : "Produit archivé"); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-heading text-2xl text-brown-black">Boutique — Produits numériques</h2>
        <button onClick={openNew} data-testid="shop-admin-add" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brown-dark text-ivory text-sm font-semibold hover:bg-brown-black transition-colors">
          <Plus size={16} /> Ajouter un produit
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p) => (
          <div key={p.id} className={`bg-white rounded-xl overflow-hidden border ${p.archived ? "border-red-200 opacity-70" : "border-gold/15"}`} data-testid={`shop-admin-product-${p.id}`}>
            {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-36 object-cover" /> : <div className="w-full h-36 bg-[#0b1436]/10 flex items-center justify-center text-brown-black/30"><ShoppingBag size={22} /></div>}
            <div className="p-4">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] uppercase tracking-wide text-gold font-semibold">{p.category}</span>
                {p.badge && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold/15 text-brown-dark font-bold">{p.badge}</span>}
                {p.status === "draft" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">Brouillon</span>}
                {p.product_type === "physical" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold">Physique</span>}
                {p.product_type === "physical" && p.stock <= 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">Rupture</span>}
                {!p.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-brown-dark/10 text-brown-dark/60 font-bold">Inactif</span>}
              </div>
              <h3 className="font-heading text-lg text-brown-black leading-snug mt-1">{p.name}</h3>
              <p className="text-gold font-semibold mt-1">{Number(p.price).toFixed(2)} €{p.variants?.length ? ` · ${p.variants.length} format(s)` : ""}{p.product_type === "physical" ? ` · stock ${p.stock}` : ""}</p>

              {p.product_type !== "physical" && !p.external_link && !(p.variants && p.variants.length) && (
                <div className={`mt-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2 border ${p.digital_file ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                  <span className={`text-xs font-semibold inline-flex items-center gap-1.5 ${p.digital_file ? "text-emerald-700" : "text-amber-700"}`}>
                    {p.digital_file ? <><CheckCircle2 size={14} /> Fichier associé</> : <><AlertCircle size={14} /> Aucun fichier</>}
                  </span>
                  <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-colors ${fileBusy === p.id ? "opacity-50 pointer-events-none" : ""} ${p.digital_file ? "border border-gold/30 text-gold hover:bg-gold/10" : "bg-gold text-white hover:bg-gold-light"}`} data-testid={`shop-quick-upload-label-${p.id}`}>
                    <FileUp size={13} /> {fileBusy === p.id ? "Envoi…" : p.digital_file ? "Remplacer" : "Téléverser l'audio/fichier"}
                    <input type="file" accept="audio/*,.pdf,.epub,.zip,.mp3,.m4a,.wav" className="hidden" onChange={(e) => quickUploadFile(p, e.target.files?.[0])} data-testid={`shop-quick-upload-${p.id}`} />
                  </label>
                </div>
              )}

              <div className="flex gap-2 mt-3">
                <button onClick={() => openEdit(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-gold/30 text-gold text-sm hover:bg-gold/10"><Pencil size={14} /> Modifier</button>
                <button onClick={() => duplicate(p.id)} data-testid={`shop-admin-duplicate-${p.id}`} title="Dupliquer" className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gold/30 text-brown-dark/70 hover:bg-gold/10"><Copy size={15} /></button>
                <button onClick={() => toggleArchive(p)} title="Archiver" className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gold/30 text-brown-dark/60 hover:bg-gold/10"><Trash size={15} /></button>
                <button onClick={() => remove(p.id)} data-testid={`shop-admin-delete-${p.id}`} className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-red-200 text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "Modifier le produit" : "Nouveau produit"}>
        <form onSubmit={save} className="space-y-4">
          <div><Lbl>Nom</Lbl><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="shop-form-name" /></div>
          <div><Lbl>Description</Lbl><textarea className={inputCls} rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="shop-form-description" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Lbl>Catégorie</Lbl><input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex : Musique" data-testid="shop-form-category" /></div>
            <div><Lbl>Badge format (ex : E-book)</Lbl><input className={inputCls} value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div><Lbl>Prix (€)</Lbl><input type="number" step="0.01" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required data-testid="shop-form-price" /></div>
            <div><Lbl>Ancien prix (€)</Lbl><input type="number" step="0.01" className={inputCls} value={form.old_price} onChange={(e) => setForm({ ...form, old_price: e.target.value })} /></div>
            <div><Lbl>Badge</Lbl>
              <select className={inputCls + " bg-white"} value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} data-testid="shop-form-badge">
                {BADGES.map((b) => <option key={b} value={b}>{b || "Aucun"}</option>)}
              </select>
            </div>
          </div>

          {/* Images */}
          <div>
            <Lbl>Images (la 1ère est la principale)</Lbl>
            <div className="flex flex-wrap gap-2 items-center">
              {form.images.map((im, i) => (
                <div key={i} className="relative">
                  <img src={im} alt="" className="w-20 h-16 object-cover rounded-lg border border-gold/20" />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center"><X size={11} /></button>
                </div>
              ))}
              <label className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gold/40 text-gold text-sm font-semibold cursor-pointer hover:bg-gold/10 ${busy ? "opacity-50 pointer-events-none" : ""}`}>
                <Upload size={15} /> Ajouter
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="shop-form-image-upload" />
              </label>
            </div>
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Lbl>Variantes / formats (optionnel)</Lbl>
              <button type="button" onClick={addVariant} className="text-xs text-gold font-semibold hover:underline" data-testid="shop-form-add-variant">+ Ajouter une variante</button>
            </div>
            <p className="text-[11px] text-brown-black/50 mb-2">Ex : E-book / Audio / Album / Single. Chaque variante a son prix et son fichier de téléchargement.</p>
            <div className="space-y-3">
              {form.variants.map((v, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 border border-gold/20 rounded-lg p-2.5">
                  <input className="flex-1 min-w-[120px] border border-gold/25 rounded px-2.5 py-2 text-sm" placeholder="Libellé" value={v.label} onChange={(e) => updVariant(i, "label", e.target.value)} data-testid={`shop-variant-label-${i}`} />
                  <input type="number" step="0.01" className="w-24 border border-gold/25 rounded px-2.5 py-2 text-sm" placeholder="Prix" value={v.price} onChange={(e) => updVariant(i, "price", e.target.value)} data-testid={`shop-variant-price-${i}`} />
                  <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-gold/30 text-gold text-xs font-semibold cursor-pointer hover:bg-gold/10">
                    <FileUp size={13} /> {v.file ? "Fichier ✓" : "Fichier"}
                    <input type="file" className="hidden" onChange={(e) => uploadDigital(e.target.files?.[0], (url) => updVariant(i, "file", url))} />
                  </label>
                  <button type="button" onClick={() => rmVariant(i)} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Digital file (no variants) */}
          {form.variants.length === 0 && (
            <div>
              <Lbl>Fichier numérique à télécharger</Lbl>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gold/40 text-gold text-sm font-semibold cursor-pointer hover:bg-gold/10">
                  <FileUp size={15} /> {form.digital_file ? "Remplacer" : "Téléverser"}
                  <input type="file" className="hidden" onChange={(e) => uploadDigital(e.target.files?.[0], (url) => setForm((f) => ({ ...f, digital_file: url }))) } data-testid="shop-form-digital-upload" />
                </label>
                {form.digital_file && <span className="text-xs text-emerald-600 font-semibold">Fichier prêt ✓</span>}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Lbl>Type de produit</Lbl>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, product_type: "digital" })}
                  className={`flex-1 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${form.product_type !== "physical" ? "bg-gold text-white border-gold" : "bg-white text-brown-dark border-gold/30"}`} data-testid="shop-form-type-digital">Numérique</button>
                <button type="button" onClick={() => setForm({ ...form, product_type: "physical" })}
                  className={`flex-1 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${form.product_type === "physical" ? "bg-gold text-white border-gold" : "bg-white text-brown-dark border-gold/30"}`} data-testid="shop-form-type-physical">Physique</button>
              </div>
            </div>
            <div>
              <Lbl>Statut</Lbl>
              <select className={inputCls + " bg-white"} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="shop-form-status">
                <option value="published">Publié</option>
                <option value="draft">Brouillon</option>
              </select>
            </div>
          </div>

          {form.product_type === "physical" && (
            <div><Lbl>Stock disponible</Lbl><input type="number" className={inputCls} value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="shop-form-stock" placeholder="Ex : 100" /></div>
          )}

          <label className="flex items-center gap-2 text-sm text-brown-dark font-semibold cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[#C9A13A]" data-testid="shop-form-active" />
            Produit actif (visible dans la boutique)
          </label>

          <button type="submit" disabled={busy} data-testid="shop-form-submit" className="w-full py-3 rounded-full bg-gold text-white font-semibold hover:bg-gold-light transition-colors disabled:opacity-50">Enregistrer</button>
        </form>
      </Modal>
    </div>
  );
};

export default ShopAdmin;
